import { css } from '@emotion/react'
import React, { useState } from 'react'
import { Checkbox, FormControlLabel } from '@mui/material'

import { ConfirmModal } from '../../../shared/modal'
import useAppSettings from '../../../../lib/hooks/useAppSettings'

export type ConfirmDeleteGroupModalProps = {
  /** Disable the delete because the group still has items associated. */
  isDisabled?: boolean
  /** Number of items in the group */
  itemCount?: number
  /** Number of locked items in the group */
  lockedItemCount?: number
  /** Whether the user can delete locked items (false for students, true for advisors/admins) */
  userCanDeleteLocked?: boolean
  /** Determines visibility of modal */
  isOpen: boolean
  /** Loading state during deletion */
  isDeleting?: boolean
  /** Progress message during deletion */
  deletionProgress?: string
  /** Event callback when Confirm Button is triggered */
  onConfirm: () => void
  /** Event callback when confirming deletion with items */
  onConfirmWithItems?: () => void
}

const checkboxContainerCss = css`
  margin-top: 16px;
  margin-bottom: 8px;
`

const messageContainerCss = css`
  margin-bottom: 8px;
`

const warningTextCss = css`
  color: #d32f2f;
  font-weight: 500;
  margin-top: 8px;
`

const progressTextCss = css`
  font-style: italic;
  margin-top: 8px;
  color: #666;
`

const ConfirmDeleteGroupModal = ({
  isDisabled: _isDisabled = false,
  itemCount = 0,
  lockedItemCount = 0,
  userCanDeleteLocked = true,
  isOpen,
  isDeleting = false,
  deletionProgress,
  onConfirm,
  onConfirmWithItems
}: ConfirmDeleteGroupModalProps) => {
  const { text } = useAppSettings()
  const termLabel = text.term
  const advisorLabel = text.advisor
  const courseLabel = text.course

  const [removeAllItems, setRemoveAllItems] = useState(false)

  const hasItems = (itemCount || 0) > 0
  const hasLockedItems = (lockedItemCount || 0) > 0
  const studentWithLockedItems = !userCanDeleteLocked && hasLockedItems

  // Reset checkbox when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setRemoveAllItems(false)
    }
  }, [isOpen])

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRemoveAllItems(event.target.checked)
  }

  const handleConfirm = () => {
    if (hasItems && removeAllItems && onConfirmWithItems) {
      onConfirmWithItems()
    } else {
      onConfirm()
    }
  }

  // Determine button disabled state
  const confirmDisabled = isDeleting || (hasItems && !removeAllItems)

  // Build the message
  let message: React.ReactNode

  if (isDeleting && deletionProgress) {
    message = (
      <div>
        <div css={messageContainerCss}>Deleting {termLabel.toLowerCase()} and all items...</div>
        <div css={progressTextCss}>{deletionProgress}</div>
      </div>
    )
  } else if (!hasItems) {
    message = `Are you sure you want to delete the ${termLabel.toLowerCase()}?`
  } else if (studentWithLockedItems) {
    message = (
      <div>
        <div css={messageContainerCss}>
          This {termLabel.toLowerCase()} contains {lockedItemCount} locked{' '}
          {lockedItemCount === 1 ? 'item' : 'items'} that cannot be deleted.
        </div>
        <div css={warningTextCss}>
          Please contact your {advisorLabel.toLowerCase()} to remove locked items first.
        </div>
      </div>
    )
  } else {
    const lockedText = hasLockedItems
      ? ` (including ${lockedItemCount} locked ${lockedItemCount === 1 ? 'item' : 'items'})`
      : ''

    message = (
      <div>
        <div css={messageContainerCss}>
          This {termLabel.toLowerCase()} contains {itemCount}{' '}
          {itemCount === 1 ? courseLabel.toLowerCase() : text.coursePlural.toLowerCase()}
          {lockedText}.
        </div>
        <div css={checkboxContainerCss}>
          <FormControlLabel
            control={
              <Checkbox
                checked={removeAllItems}
                onChange={handleCheckboxChange}
                disabled={isDeleting}
                color="primary"
              />
            }
            label={`Remove all items and delete ${termLabel.toLowerCase()}`}
          />
        </div>
      </div>
    )
  }

  const confirmText = isDeleting ? `Deleting ${termLabel}...` : `Delete ${termLabel}`

  return (
    <ConfirmModal
      confirmButtonDisabled={confirmDisabled}
      confirmText={confirmText}
      isDanger
      isOpen={isOpen}
      onConfirm={handleConfirm}
      title={`Delete ${termLabel}`}
    >
      {message}
    </ConfirmModal>
  )
}

export default ConfirmDeleteGroupModal
